import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Crown, Check, X, Star, Tag,
} from "lucide-react";

const EMPTY = {
  name: "", price: "", duration_days: "", description: "",
  features: "", featured: false, active: true, tag: "",
};

function Field({ label, children, hint }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      {children}
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

function Modal({ pkg, onClose, onSaved }) {
  const isEdit = !!pkg?.id;
  const [form, setForm] = useState(
    pkg
      ? { ...pkg, features: (pkg.features || []).join(", "), price: String(pkg.price), duration_days: String(pkg.duration_days) }
      : EMPTY
  );
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.price || !form.duration_days) {
      toast.error("Name, Price and Duration are required");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        price: parseInt(form.price, 10),
        duration_days: parseInt(form.duration_days, 10),
        description: form.description || "",
        features: form.features.split(/[\n,]+/).map((f) => f.trim()).filter(Boolean),

        featured: form.featured,
        active: form.active,
        tag: form.tag || null,
      };
      if (isEdit) {
        await api.put(`/admin/packages/${pkg.id}`, body);
        toast.success("Package updated");
      } else {
        await api.post("/admin/packages", body);
        toast.success("Package created");
      }
      onSaved();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">
            {isEdit ? "Edit Package" : "New Package"}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <Field label="Package Name">
            <Input value={form.name} onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Gold Premium" />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Price (₹ INR)">
              <Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)}
                placeholder="e.g. 1299" />
            </Field>
            <Field label="Duration (days)">
              <Input type="number" value={form.duration_days} onChange={(e) => set("duration_days", e.target.value)}
                placeholder="e.g. 90" />
            </Field>
          </div>

          <Field label="Short Description">
            <Input value={form.description} onChange={(e) => set("description", e.target.value)}
              placeholder="Best value for serious search." />
          </Field>

          <Field label="Badge / Tag" hint="Optional — e.g. 'Most Popular' shown on card">
            <Input value={form.tag || ""} onChange={(e) => set("tag", e.target.value)}
              placeholder="Most Popular" />
          </Field>

          <Field label="Features" hint="Comma-separated list of features">
            <textarea
              value={form.features}
              onChange={(e) => set("features", e.target.value)}
              rows={4}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              placeholder="Browse Profiles, Send Interest, View contact details, Advanced filters"
            />
          </Field>

          <div className="flex gap-8 pt-1">
            <div className="flex items-center gap-3">
              <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} />
              <Label className="text-sm text-slate-600 cursor-pointer">Featured (highlighted)</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} />
              <Label className="text-sm text-slate-600 cursor-pointer">Active</Label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 pb-6 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-white hover:bg-primary/90">
            {saving ? "Saving…" : isEdit ? "Update Package" : "Create Package"}
          </Button>
        </div>
      </div>
    </div>
  );
}

const PLAN_COLORS = {
  monthly: "bg-blue-50 text-blue-700",
  quarterly: "bg-amber-50 text-amber-700",
  yearly: "bg-purple-50 text-purple-700",
};

export default function AdminPackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {} (new) | {pkg} (edit)
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/packages");
      setPackages(data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (pkg) => {
    if (!window.confirm(`Delete "${pkg.name}"? This cannot be undone.`)) return;
    setDeleting(pkg.id);
    try {
      await api.delete(`/admin/packages/${pkg.id}`);
      toast.success("Package deleted");
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Membership Packages"
        subtitle="Create and manage subscription plans shown on the homepage and premium page."
        action={
          <Button onClick={() => setModal({})} className="bg-primary text-white hover:bg-primary/90 gap-2">
            <Plus className="w-4 h-4" /> New Package
          </Button>
        }
      />

      {loading ? (
        <div className="text-slate-400 py-20 text-center">Loading packages…</div>
      ) : packages.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 py-20 text-center">
          <Crown className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No packages yet.</p>
          <Button onClick={() => setModal({})} variant="outline" className="mt-4 gap-2">
            <Plus className="w-4 h-4" /> Create your first package
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`relative bg-white rounded-2xl border p-6 flex flex-col shadow-sm transition hover:shadow-md
                ${pkg.featured ? "border-primary ring-2 ring-primary/20" : "border-slate-100"}`}
            >
              {/* Badges */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex flex-col gap-1">
                  {pkg.tag && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-600 font-semibold">
                      <Star className="w-3 h-3" />{pkg.tag}
                    </span>
                  )}
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium
                    ${pkg.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {pkg.active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setModal(pkg)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-primary hover:bg-primary/5 transition"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(pkg)}
                    disabled={deleting === pkg.id}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Name & Price */}
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-heading text-xl text-primary font-bold">{pkg.name}</span>
                {pkg.featured && <Crown className="w-4 h-4 text-amber-500" />}
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-bold text-slate-800">₹{pkg.price.toLocaleString()}</span>
                <span className="text-sm text-slate-400">/ {pkg.duration_days} days</span>
              </div>
              {pkg.description && (
                <p className="text-xs text-slate-500 mb-4">{pkg.description}</p>
              )}

              {/* Features */}
              <ul className="space-y-1.5 flex-1">
                {(pkg.features || []).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {modal !== null && (
        <Modal
          pkg={modal?.id ? modal : null}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
